/**
 * This class fires events for dropdown menus
 *
 * - supports PointerEvents
 * - supports HoverIntent
 * - prevents following the first link on touch/keyboard click unless already opened
 * - deactivates when blurred for any event
 *
 * Use this by either passing callbacks.activate, callbacks:deactivate, * or by
 * listening for the dropdown:activate or dropdown:deactivate events on the
 * passed node
 *
 * Events Example:
 * ```
 * dropdownEvent(node);
 * node.addEventListener('dropdown:activate', () => {
 *   // add dropdown classes
 * });
 * node.addEventListener('dropdown:deactivate', () => {
 *   // remove dropdown classes
 * });
 * ```

 * Callbacks Example:
 * ```
 * dropdownEvent(node, {
 *   activate: function() {
 *     // add dropdown classes
 *   },
 *   deactivate: function() {
 *     // remove dropdown classes
 *   },
 * });
 * ```
 *
 * jQuery events example
 * ```
 * jQuery('selector').dropdownEvent().on('dropdown:activate', function() {
 *   // add dropdown classes
 * }).on('dropdown:deactivate', function() {
 *   // remove dropdown classes
 * });
 * ```
 *
 * jQuery callbacks example
 * ```
 * jQuery('selector').dropdownEvent({
 *   activate: function() {
 *     // add dropdown classes
 *   },
 *   deactivate: function() {
 *     // remove dropdown classes
 *   }
 * });
 * ```
 *
 * @class
 */

export class DropdownEvent {
	/**
	 *
	 * @param HTMLElement node
	 * @param {Object} opts
	 * @param {Integer} [opts.delay=200] - the hover intent delay on mouse events
	 * @param {Function} opts.activate - the callback function for activate
	 * @param {Function} opts.deactivate - the callback function for deactivate
	 * @param {String} [opts.activateEvent=dropdown:activate] - the name of the activate event to listen to
	 * @param {String} [opts.deactivateEvent=dropdown:deactivate] - the name of the deactivate event to listen to
	 */
	constructor(node, opts = {}) {
		this.node = node;
		this.opts = {
			delay: 200,
			activate: () => {},
			deactivate: () => {},
			activateEvent: 'dropdown:activate',
			deactivateEvent: 'dropdown:deactivate',
		};

		for (let opt in opts) {
			if (opts.hasOwnProperty(opt)) {
				this.opts[opt] = opts[opt];
			}
		}

		this.active       = false;
		this.enterTimeout = null;
		this.leaveTimeout = null;

		// prevents the click event from following links (used by touch/keyboard handlers)
		const preventClickEvent = function(e) {
			e.preventDefault();
			e.stopPropagation();
			this.removeEventListener('click', preventClickEvent);
		}

		// activate if the event target is a child link
		const maybeActivate = (e) => {
			if (this.active) {
				return;
			}

			let target = e.target;
			while(target.parentNode && target !== this.node) {
				if (target.tagName === 'A') {
					target.addEventListener('click', preventClickEvent);
				}

				target = target.parentNode;
			}

			this.activate(e);
		}

		// activate on mouse enter and apply delay
		const mouseenter = (e) => {
			if (typeof this.leaveTimeout === 'number') {
				return window.clearTimeout(this.leaveTimeout);
			}

			this.enterTimeout = setTimeout(() => {
				this.activate(e);
			}, this.opts.delay);
		}

		// deactivate on mouse leave and apply delay
		const mouseleave = (e) => {
			if (typeof this.enterTimeout === 'number') {
				return window.clearTimeout(this.enterTimeout);
			}

			this.leaveTimeout = window.setTimeout(() => {
				this.deactivate(e);
			}, this.opts.delay);
		}

		// handle the touch start event
		const touchstart = (e) => {
			return maybeActivate(e);
		}

		// handle the keyup event
		const keyup = (e) => {
			if ( 'enter' === e.key.toLowerCase() ) {
				return maybeActivate(e);
			}

			return null;
		}

		// handle the pointer enter and route to touch/mouse event ( treats pen events as mouse )
		const pointerenter = (e) => {
			return 'touch' === e.pointerType ? touchstart(e) : mouseenter(e);
		}

		// handle the pointer leave and route to touch/mouse event ( treats pen events as mouse )
		const pointerleave = (e) => {
			return 'touch' === e.pointerType ? touchstart(e) : mouseleave(e);
		}

		// on captured blur detect if the event target is a child of this.node, if not, deactivate
		const blur = () => {
			if ( ! this.active ) {
				return;
			}

			window.setTimeout(() => {
				let target = document.activeElement;
				while ( target && target.parentNode ) {
					if ( target === this.node ) {
						return;
					}

					target = target.parentNode;
				}

				this.deactivate();
			}, 0);
		}

		if ( window.PointerEvent ) {
			// add pointer events
			this.node.addEventListener('pointerenter', pointerenter);
			this.node.addEventListener('pointerleave', pointerleave);
		} else {
			// no pointer events - use mouse/touch events directly
			this.node.addEventListener('touchstart', touchstart);
			this.node.addEventListener('mouseenter', mouseenter);
			this.node.addEventListener('mouseleave', mouseleave);
		}

		// add keyup/blur events
		this.node.addEventListener('keyup', keyup);
		this.node.addEventListener('blur', blur, true); // capture
	}

	/**
	 * Activates the dropdown and fires the callback/event
	 *
	 * @param {Object} e - passed along to the callback/event as detail
	 */
	activate(e = {}) {
		this.active = true;

		if (typeof this.enterTimeout === 'number') {
			window.clearTimeout(this.enterTimeout);
			this.enterTimeout = null;
		}

		if (typeof this.opts.activate === 'function') {
			this.opts.activate.call(this.node, e);
		}

		if (typeof this.opts.activateEvent === 'string') {
			this.dispatchEvent(this.opts.activateEvent);
		}
	}

	/**
	 * Deactivates the dropdown and fires the callback/event
	 *
	 * @param {Object} e - passed along to the callback/event as detail
	 */
	deactivate(e = {}) {
		this.active = false;

		if (typeof this.leaveTimeout === 'number') {
			window.clearTimeout(this.leaveTimeout);
			this.leaveTimeout = null;
		}

		if (typeof this.opts.deactivate === 'function') {
			this.opts.deactivate.call(this.node, e);
		}

		if (typeof this.opts.deactivateEvent === 'string') {
			this.dispatchEvent(this.opts.deactivateEvent);
		}
	}

	dispatchEvent(eventName, node = this.node) {
		let event;
		if (typeof window.Event === 'function') {
			event = new Event(eventName);
		} else {
			event = document.createEvent('Event');
			event.initEvent(eventName, true, false);
		}

		node.dispatchEvent(event);
	}
}

/**
 * This is a factory for the DropdownEvent class that supports nodelists/arrays/selectors
 *
 * @param {HTMLElement|String|NodeList|Array} node
 * @param {opts} opts - see DropdownEvent
 * @return mixed DropdownEvent|Array|false
 *
 * @uses DropdownEvent
 * @uses Array.from
 * @uses Array.isArray
 */
export function dropdownEvent(node, opts = {}) {
	if (node instanceof HTMLElement) {
		return new DropdownEvent(node, opts);
	}

	const nodes = (typeof node === 'string') ? Array.from(document.querySelectorAll(node)) : (node.length) ? Array.from(node) : [];

	if (Array.isArray(nodes)) {
		return nodes.map((n) => new DropdownEvent(n, opts));
	}

	return false;
}

/**
 * Add a jQuery function for those who prefer that way
 */
if (typeof window.jQuery === 'function') {
	window.jQuery.fn.dropdownEvent = function(opts) {
		return this.each(function() {
			const $this = window.jQuery(this);
			$this.data('dropdownEvent', new DropdownEvent(this, opts));
		});
	};
}

export default dropdownEvent;
